import type { FC } from 'react';

export interface PillItem {
    key: string;
    label: string;
    /** Absent while counts load: the pill renders without a number. */
    count?: number;
}

/**
 * One level of the drill-down filter. Wraps rather than scrolls: the whole point is seeing every
 * option at once — a horizontal scroller hides exactly the choice you're looking for.
 *
 * Counts are computed against the filters ABOVE this row, not this row's own selection, so a pill
 * always answers "how many would I get if I clicked this".
 */
export const FilterPills: FC<{
    label: string;
    items: PillItem[];
    selected: string | undefined;
    allCount?: number;
    onSelect: (key: string | undefined) => void;
}> = ({ label, items, selected, allCount, onSelect }) => (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2">
        <span className="w-16 shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
        <div className="flex flex-1 flex-wrap gap-2">
            <Pill label="All" count={allCount} active={selected === undefined} onClick={() => onSelect(undefined)} />
            {items.map((item) => (
                <Pill
                    key={item.key}
                    label={item.label}
                    count={item.count}
                    active={selected === item.key}
                    onClick={() => onSelect(selected === item.key ? undefined : item.key)}
                />
            ))}
        </div>
    </div>
);

/**
 * Pills for the options that have rows, in `keys` order. The selected option stays even at zero, so the
 * active filter never disappears out from under the list it's filtering. Until `counts` loads, only the
 * selected option shows, without a count; a count missing from a loaded set is unknown (that one
 * aggregation failed), so its pill stays, without a number.
 */
export function nonEmptyPills<K extends string>(
    keys: readonly K[],
    labels: Record<K, string>,
    counts: Partial<Record<K, number>> | undefined,
    selected: K | undefined,
): PillItem[] {
    if (!counts) {
        return selected ? [{ key: selected, label: labels[selected] }] : [];
    }
    return keys
        .filter((key) => counts[key] === undefined || counts[key] > 0 || key === selected)
        .map((key) => ({ key, label: labels[key], count: counts[key] }));
}

/** A dropdown option's label with its row count, once the count has loaded. */
export function withCount(label: string, count: number | undefined): string {
    return count === undefined ? label : `${label} (${count.toLocaleString()})`;
}

/**
 * A count cell's three states: `…` while the counts are loading, the number, and `—` once the counts have
 * settled without this one (that aggregation failed; the others still show). `settled` is "the counts
 * object arrived, or every count failed".
 */
export function formatCount(count: number | undefined, settled: boolean): string | number {
    return settled ? (count ?? '—') : '…';
}

const Pill: FC<{ label: string; count?: number; active: boolean; onClick: () => void }> = ({
    label,
    count,
    active,
    onClick,
}) => (
    <button
        type="button"
        onClick={onClick}
        className={`cursor-pointer rounded-full border px-3 py-1 text-sm transition-colors ${
            active
                ? 'border-primary bg-primary text-primary-foreground'
                : // An empty option stays clickable — picking it is how you get to "add one of these" —
                  // but reads as muted so a full row of real options doesn't compete with the gaps.
                  `border-input hover:bg-accent ${count === 0 ? 'text-muted-foreground' : ''}`
        }`}
    >
        {label}
        {count !== undefined && (
            <span className={active ? 'opacity-80' : 'text-muted-foreground'}> ({count.toLocaleString()})</span>
        )}
    </button>
);
